import React from 'react';
import { SelectItem, ToggleGroupItem } from '@highereducation/hen-ui';

/**
 * Turning a resolved option list into the child nodes a compound hen-ui
 * control needs.
 *
 * `_bind.options` with `transform: 'options'` resolves to a flat
 * `{ label, value }[]`, which is the shape a document can describe. Two of the
 * three hen-ui choice controls cannot consume it: `Select` is a Radix listbox
 * that reads `SelectItem` children, and `ToggleGroup` reads `ToggleGroupItem`
 * children. Only `MultiSelect` takes an `options` array natively, which is why
 * it has no entry below — the table lists the controls that need the
 * conversion, not the controls that accept options.
 *
 * This lives outside the DSL step variant because it is a property of the
 * hen-ui contract, not of the surface rendering it. It was previously inline in
 * the DSL's component registration, which meant a second host registering the
 * same hen-ui components — the partner dashboard — got a `Select` that
 * rendered an empty listbox for a correctly authored binding.
 *
 * It is deliberately not part of the binding enhancer. The enhancer is behind
 * the `./ce-runtime` entry point, whose whole purpose is emitting data rather
 * than building UI, and a hen-ui value import there is a guarded regression.
 */

interface OptionEntry {
  readonly label: unknown;
  readonly value: unknown;
}

type OptionItemComponent = React.ComponentType<{
  value: string;
  children?: React.ReactNode;
}>;

/**
 * Controls whose option list arrives as data and has to leave as children,
 * mapped to the item component that carries one option.
 *
 * Adding a third control is one row. `MultiSelect` must not be added: it reads
 * `options` directly, and converting its list to children would empty it.
 */
const OPTION_CHILD_ITEM: Readonly<Record<string, OptionItemComponent>> = {
  Select: SelectItem as OptionItemComponent,
  ToggleGroup: ToggleGroupItem as unknown as OptionItemComponent,
};

/** The shape `transform: 'options'` produces. */
function isOptionsArray(value: unknown): value is readonly OptionEntry[] {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        'label' in (entry as object) &&
        'value' in (entry as object),
    )
  );
}

const hasAuthoredChildren = (children: unknown): boolean =>
  children !== undefined &&
  children !== null &&
  !(Array.isArray(children) && children.length === 0);

/** Component types this conversion applies to, for a host to assert against. */
export const OPTION_CHILD_COMPONENT_TYPES: readonly string[] =
  Object.keys(OPTION_CHILD_ITEM);

/**
 * Returns `props` with a resolved `options` list replaced by the item children
 * the control reads, or the same object when there is nothing to convert.
 *
 * Returning the argument unchanged for the common case is deliberate: every
 * host calls this for every node of every type, and an untouched component
 * should not pay an allocation or lose prop identity for it.
 *
 * A bound option list wins over authored children. Authoring both is
 * contradictory — the author asked for the list by binding it — and the
 * alternative rule fails in a way that is much harder to see: Puck infers slot
 * fields per type across the whole document, so one `Select` elsewhere
 * authoring child nodes would turn `children` into a render function on every
 * `Select`, and a rule that yields to authored children would silently drop the
 * bound options on a node that has none.
 *
 * An empty list does not replace authored children. Saved documents carry a
 * static `options: []` beside hand-authored items, and swapping those items
 * for nothing leaves a menu that opens empty. An empty slot renders nothing
 * either way, so a bound list that resolves empty still shows no options.
 */
export function synthesizeOptionChildren(
  type: string,
  props: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const Item = OPTION_CHILD_ITEM[type];
  if (!Item || !isOptionsArray(props.options)) {
    return props as Record<string, unknown>;
  }

  const { options, ...rest } = props;
  if (props.options.length === 0 && hasAuthoredChildren(rest.children)) {
    return rest;
  }
  return {
    ...rest,
    children: (options as readonly OptionEntry[]).map((entry, index) => (
      <Item key={`${String(entry.value)}-${index}`} value={String(entry.value)}>
        {String(entry.label)}
      </Item>
    )),
  };
}
